import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import propTypes from 'prop-types';
import FontAwesome from 'react-fontawesome';

import Button from '~/Components/Global/Form/Button';
import Tooltip from '~/Components/Tooltip';
import { setProduct, getStickerPacks, getSkins } from '~/Redux/Modules/Products';
import StripePermittedView from '~/Components/Global/StripePermittedView';
import { usePaddle, usePaddleCheckout } from '~/util';
import { productApi } from '~/Providers/Api';

import './Product.scss';

const Product = ({ product, description, type }) => {
  const dispatch = useDispatch();
  const usePaddleValue = usePaddle();
  const pollingInterval = useRef(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingTime, setPollingTime] = useState(10000);

  const { openCheckout, closeCheckout } = usePaddleCheckout({
    settings: {
      variant: 'one-page',
    },
    onEvent: event => {
      if (event.name === 'checkout.completed') {
        startPolling();
        setPollingTime(1000);
      }
    },
  });

  useEffect(() => {
    console.log('Product', 'useEffect', pollingInterval.current);
    pollingInterval.current = setInterval(async () => {
      console.log('Product', 'polling for purchase');
      if (type === 'STICKER_PACKS') {
        const { data: stickerPacks } = await dispatch(getStickerPacks());
        const updatedProduct = stickerPacks.find(p => p.product?.id === product.id);
        if (updatedProduct?.product?.purchased) {
          console.log('Product', 'clearing polling interval');
          closeCheckout();
          clearInterval(pollingInterval.current);
          setIsPolling(false);
        }
      } else if (type === 'SKINS') {
        const { data: skins } = await dispatch(getSkins());
        const updatedProduct = skins.find(p => p.product?.id === product.id);
        if (updatedProduct?.product?.purchased) {
          console.log('Product', 'clearing polling interval');
          closeCheckout();
          clearInterval(pollingInterval.current);
          setIsPolling(false);
        }
      }
    }, pollingTime);

    return () => {
      console.log('Product', 'useEffect release', pollingInterval.current);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [dispatch, type, product.id, closeCheckout, pollingTime, pollingInterval]);

  const startPolling = useCallback(() => {
    setIsPolling(true);
  }, []);

  const formatPrice = price => {
    return `$${price.toFixed(2)}`;
  };

  const handlePurchase = async () => {
    if (usePaddleValue) {
      try {
        const { data: paddleData } = await productApi.purchase(product.id, {
          platform: 'paddle',
        });

        openCheckout({
          transactionId: paddleData.transaction_id,
          customerAuthToken: paddleData.customer_auth_token,
          customer: {
            id: paddleData.customer_id,
          },
        });
      } catch (error) {
        console.error('Failed to initialize Paddle checkout:', error);
      }
    } else {
      dispatch(setProduct({ ...product, description }, type));
    }
  };

  const renderPurchaseStatus = () => {
    if (!product) return null;
    if (!product.purchased) return null;
    if (product.unlock_code && (product.isFree || product.unlocked))
      return <div className="purchased">UNLOCKED!</div>;
    if (product.isFree) return null;
    return <div className="purchased">PURCHASED!</div>;
  };

  const renderProductLabel = () => {
    if (!product) return 'My Stickers';
    if (product.isFree && !product.unlock_code) return 'FREE';
    if (product.isFree && product.unlock_code) return 'UNLOCKABLE';
    if (product.is_fundraiser)
      return (
        <>
          <span>FUNDRAISER</span>
          <Tooltip>
            {type == 'STICKER_PACKS'
              ? "100% of proceeds will go to Feeding America's COVID-19 fund."
              : '100% of proceeds will go to Direct Relief to supply personal protective equipment and essential medical items to those who need them most.'}
          </Tooltip>
        </>
      );
    if (product.unlocked) return 'UNLOCKABLE';
    return 'PREMIUM';
  };

  return (
    <div className="product">
      <div className="premium center">
        <FontAwesome className="star" name="star" size="lg" />
        {renderProductLabel()}
      </div>
      {renderPurchaseStatus()}
      {product && !product.purchased && (
        <StripePermittedView>
          <Button
            className="pill"
            text={`${formatPrice(product.price)} Purchase`}
            onClick={handlePurchase}
          />
        </StripePermittedView>
      )}
    </div>
  );
};

Product.propTypes = {
  product: propTypes.oneOfType([propTypes.bool, propTypes.object]),
  type: propTypes.string,
  description: propTypes.string,
};

Product.defaultProps = {};

export default Product;
